import styled from 'styled-components';
import { IPerformanceDB } from 'type/tracking';

import { useGetContentSummary } from 'hooks/queries/tracking';
import CoreLoading from 'shared/CoreLoading';
import {
	EmphasizeText,
	ReportSection,
	ReportSectionCard,
	ReportTopHeader,
} from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

const METRIC_LABELS: {
	key: keyof IPerformanceDB;
	avgLabel: string;
	totalLabel: string;
	suffix: string;
}[] = [
	{ key: 'viewCount', avgLabel: '평균 조회 수', totalLabel: '총 조회 수', suffix: '회' },
	{
		key: 'likeCount',
		avgLabel: '평균 좋아요 수',
		totalLabel: '총 좋아요 수',
		suffix: '개',
	},
	{
		key: 'commentCount',
		avgLabel: '평균 댓글 수',
		totalLabel: '총 댓글 수',
		suffix: '개',
	},
	{ key: 'shareCount', avgLabel: '평균 공유 수', totalLabel: '총 공유 수', suffix: '회' },
	{
		key: 'engagementCount',
		avgLabel: '평균 인게이지먼트',
		totalLabel: '총 인게이지먼트',
		suffix: '개',
	},
];

const formatMetricValue = (value: number) => {
	return (value ?? 0).toLocaleString();
};

interface IPerformanceCards {
	code: string;
}

const PerformanceCards = ({ code }: IPerformanceCards) => {
	const { data: contentSummary, isLoading } = useGetContentSummary(code);

	if (isLoading) {
		return (
			<ReportSection _margin="0">
				<Grid flex="center" _height="200px">
					<CoreLoading color_type="black" size_type="large" />
				</Grid>
			</ReportSection>
		);
	}

	if (!contentSummary || !contentSummary.averagePerformance) return null;

	const { averagePerformance, totalPerformance } = contentSummary;

	return (
		<ReportSection _margin="0">
			<PerformanceGroup>
				<GroupTitle>평균 성과</GroupTitle>
				<DataContainer>
					{METRIC_LABELS.map(({ key, avgLabel, suffix }) => (
						<ReportSectionCard key={key} _padding="16px 20px">
							<Grid flex="start" align_items="start" direction="column">
								<ReportTopHeader>{avgLabel}</ReportTopHeader>
								<EmphasizeText>
									<span>
										{formatMetricValue(averagePerformance[key])}
										<Unit> {suffix}</Unit>
									</span>
								</EmphasizeText>
							</Grid>
						</ReportSectionCard>
					))}
				</DataContainer>
			</PerformanceGroup>

			<PerformanceGroup>
				<GroupTitle>누적 성과</GroupTitle>
				<DataContainer>
					{METRIC_LABELS.map(({ key, totalLabel, suffix }) => (
						<ReportSectionCard key={key} _padding="16px 20px">
							<Grid flex="start" align_items="start" direction="column">
								<ReportTopHeader>{totalLabel}</ReportTopHeader>
								<EmphasizeText>
									<span>
										{formatMetricValue(totalPerformance[key])}
										<Unit> {suffix}</Unit>
									</span>
								</EmphasizeText>
							</Grid>
						</ReportSectionCard>
					))}
				</DataContainer>
			</PerformanceGroup>
		</ReportSection>
	);
};

const DataContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(5, 1fr);
	gap: 16px;
`;

const PerformanceGroup = styled.div`
	& + & {
		margin-top: 24px;
	}
`;

const GroupTitle = styled.div`
	${({ theme }) => theme.typo.body.p_bold};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	margin-bottom: 12px;
`;

const Unit = styled.span`
	font-size: 14px !important;
	font-weight: 400 !important;
	color: ${({ theme }) => theme.colors.gray.gray50} !important;
`;

const { Grid } = style;

export default PerformanceCards;
